import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Alert, Button, Modal, Spin, Tooltip, message } from "antd";
import ClientDiagnosisFormDrawer from "./client-diagnosis-form-drawer";
import { deleteHealthClientDiagnosis, fetchHealthClientDiagnosis } from "./health-clients-api";
import { pick, toNumber } from "./health-clients-utils";
import "./client-diagnosis.css";

const normalizeRow = (row) => {
  const idClient = toNumber(pick(row, "idClient", "IdClient"));
  const idStr = toNumber(pick(row, "idStr", "IdStr"));
  const vid = toNumber(pick(row, "vid", "Vid")) ?? 1;

  return {
    key: `${idClient}-${idStr}`,
    idClient,
    idStr,
    kod: pick(row, "kod", "Kod") ?? "",
    name: pick(row, "name", "Name") ?? "",
    name2: pick(row, "name2", "Name2") ?? "",
    vid,
    vidName: pick(row, "vidName", "VidName") ?? "",
    isPrimary: vid === 0
  };
};

const DiagnosisCard = ({ item, onEdit, onDelete }) => (
  <li>
    <div
      className={`client-diagnosis__card${item.isPrimary ? " client-diagnosis__card--primary" : ""}`}
      role="button"
      tabIndex={0}
      aria-label={`Изменить: ${[item.kod, item.name].filter(Boolean).join(" ") || "диагноз"}`}
      onClick={onEdit}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onEdit();
        }
      }}
    >
      <div className="client-diagnosis__content">
        <div className="client-diagnosis__head">
          <div className="client-diagnosis__title-block">
            <h4 className="client-diagnosis__title">
              {item.kod ? <span className="client-diagnosis__kod">{item.kod}</span> : null}
              <span className="client-diagnosis__name">{item.name || "Без названия"}</span>
              {item.vidName ? (
                <span
                  className={`client-diagnosis__badge${
                    item.isPrimary ? " client-diagnosis__badge--primary" : ""
                  }`}
                >
                  {item.vidName}
                </span>
              ) : null}
            </h4>
          </div>
          <div className="client-diagnosis__head-right" onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Удалить">
              <Button
                type="text"
                size="small"
                danger
                className="client-diagnosis__action"
                icon={<DeleteOutlined />}
                onClick={onDelete}
              />
            </Tooltip>
          </div>
        </div>
        {item.name2 ? (
          <div className="client-diagnosis__clarification">
            <span className="client-diagnosis__label">Уточнение</span>
            <span>{item.name2}</span>
          </div>
        ) : null}
      </div>
    </div>
  </li>
);

/**
 * Вкладка «Диагноз»: список карточек, редактирование в drawer.
 */
const ClientDiagnosis = ({ idClient, open = true }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) => {
        if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
        return (a.idStr ?? 0) - (b.idStr ?? 0);
      }),
    [rows]
  );

  const load = useCallback(() => {
    if (!idClient || open === false) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchHealthClientDiagnosis(idClient)
      .then((data) => {
        if (cancelled) return;
        setRows((Array.isArray(data) ? data : []).map(normalizeRow).filter((r) => r.idStr != null));
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message || "Не удалось загрузить диагнозы");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [idClient, open]);

  useEffect(() => {
    const cleanup = load();
    return () => cleanup?.();
  }, [load]);

  useEffect(() => {
    setDrawerOpen(false);
    setEditingItem(null);
  }, [idClient]);

  const openAdd = () => {
    setEditingItem(null);
    setDrawerOpen(true);
  };

  const openEdit = (row) => {
    setEditingItem(row);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingItem(null);
  };

  const handleDelete = (row) => {
    Modal.confirm({
      title: "Удалить диагноз?",
      content: [row.kod, row.name].filter(Boolean).join(" — ") || `№${row.idStr}`,
      okText: "Удалить",
      okButtonProps: { danger: true },
      cancelText: "Отмена",
      onOk: async () => {
        try {
          await deleteHealthClientDiagnosis(idClient, row.idStr);
          message.success("Диагноз удалён");
          load();
        } catch (e) {
          message.error(e.message || "Не удалось удалить");
          return Promise.reject(e);
        }
      }
    });
  };

  if (!idClient) {
    return <div className="client-tab client-tab--placeholder">Выберите клиента</div>;
  }

  return (
    <div className="client-tab client-diagnosis">
      <div className="client-diagnosis__toolbar">
        <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
          Добавить
        </Button>
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message={error}
          action={<Button onClick={load}>Повторить</Button>}
          className="client-diagnosis__error"
        />
      ) : null}

      <div className="client-diagnosis__body">
        {loading && !rows.length ? (
          <div className="client__center">
            <Spin tip="Загрузка..." />
          </div>
        ) : !rows.length ? (
          <div className="client__center">Нет диагнозов</div>
        ) : (
          <ul className="client-diagnosis__list">
            {sortedRows.map((row) => (
              <DiagnosisCard
                key={row.key}
                item={row}
                onEdit={() => openEdit(row)}
                onDelete={() => handleDelete(row)}
              />
            ))}
          </ul>
        )}
      </div>

      <ClientDiagnosisFormDrawer
        open={drawerOpen}
        idClient={idClient}
        item={editingItem}
        onClose={closeDrawer}
        onSaved={load}
      />
    </div>
  );
};

export default ClientDiagnosis;
